import { useState, useEffect, useRef } from 'react'
import { useNavigate, Navigate, useLocation } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { Building2, ChevronRight, Eye, Home, Share2, Stethoscope, Check, AlertTriangle, X, MapPin, Phone, Printer } from 'lucide-react'
import { useCaseStore, useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import { ChatKeyframes, CompactHeader, OrellaChatBubble } from '../../components/OrellaChatHeader'
import VisitPrepDisclaimer from '../../components/VisitPrepDisclaimer'
import SharePassModal from '../../components/SharePassModal'
import SwitchDoctorSheet from '../../components/share/SwitchDoctorSheet'
import ContinueWithoutInsurance from '../../components/share/ContinueWithoutInsurance' // PROD-1132 Q1
import CaseReportViewer from '../../components/share/CaseReportViewer'
import { OverlayPortal } from '../../components/ModalPortal'
import { casesAPI } from '../../services/api'
import { missingBeforeShare } from '../../utils/profileCompletion'
import { buildClaimUrl } from '../../utils/claimUrl'
import { caseBack } from '../../utils/caseBack' // PROD-654: back = the previous case screen
import { BUILD_STEPS, buildCaseReport, formatUsPhone } from '../../utils/caseReport'
import { currentOfficeSend, unseenFailedSend, officeLines, readSeenFailures, markFailureSeen } from '../../utils/officeSend'
import { withdrawCaseFromDoctor } from '../../utils/withdrawCase'
import { safePath } from '../../utils/safePath' // PROD-1362 follow-up: state.next.to
import { ANSWERS_LOCKED_STATUSES } from '../../utils/describeLock'
import { readPreferredOffice, clearPreferredOffice } from '../../utils/preferredOffice' // s106
import { clinicianName, withCaseClinician, clinicianForms, clinicianFromText } from '../../utils/clinicianName.js'
import ShareRiskSheet from '../../components/share/ShareRiskSheet' // s119 P1
import { appLocale } from '../../utils/appLanguage'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner
import BottomSheet from '../../components/BottomSheet' // s126: who opened the case
import ConfirmModal from '../../components/ConfirmModal' // s126: revoke asks once
import { canWithdrawCase } from '../../utils/canWithdraw' // s126: revoke only where a withdraw is allowed
import { resolveShareStatus } from '../../utils/caseShareState' // s126b: a code-shared case is 'shared', not 'ready_to_share'
import orellaOIcon from '../../assets/logos/orella_o_icon.png'

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const LBLUE = '#A7D9FC'
const ICE = '#E9F8FF'
const BODY = '#5A7185'
const MUTE = '#93A5B4'

/**
 * ShareHubScreen — s98 Phase 4 (D462, D468). Approved design: "Orella Summary and Share",
 * mockup C v3, screens "Building your Case Report" and "Your Case Report".
 *
 * One place every share entry lands. In order:
 *   1. Before we send: identification, insurance, allergies and medications must be on file
 *      (missingBeforeShare, the rule DiagnoseScreen already applies before a code).
 *   2. Building your Case Report: the hand-off summary, then the share code (minted once).
 *   3. The hub: the report card (View PDF behind today's Document Sharing Agreement; one muted
 *      code line with Copy and Share link), "Send to your doctor's office" (a button to the
 *      finder, then a fax or an in-app send), "Need a Doctor?" (the Orella doctor picker,
 *      ShareScreen's find-doctor step) and Case Home.
 * ★ D469 (Dr. Rami 2026-10-10): the office card's search field was not a field, so it is a
 *   button; "Visit pass" became "Share link" (the claim link on the share sheet, copied where
 *   there is none); and "Need a Doctor?" opens the Orella doctor list even though the hub has
 *   already minted a code (ShareScreen otherwise jumps straight to its share-code step). Faxes already sent show with their status; a failed one offers Try again.
 * ★ Two doctors are allowed (his ruling 2026-10-07): the hub stays usable after a fax.
 * ★ s99 D475 (Dr. Rami 2026-10-10, "Orella Shared Office"): the office the report went to
 *   REPLACES "Send to your doctor's office" (utils/officeSend.js picks it; the server marks which
 *   send is current). A failed send leaves no row: one notice, then the send row comes back.
 *   "Send to a different office" withdraws the case first (withdrawCaseFromDoctor: the code dies,
 *   the answers reopen), builds a new code and opens Find your doctor.
 * ★ s99 D476 (Dr. Rami 2026-10-10, "Orella Switch Doctor"): ONE LIVE SHARE AT A TIME. With an
 *   office already holding the report, "Need a Doctor?" asks first (SwitchDoctorSheet) and
 *   withdraws before the Orella list opens. A caller may pass `state.next = { to, state }`: the
 *   hub makes sure a code exists, then goes there (the Orella search's directory picks and its
 *   "Don't see your doctor?" need a code on the cover sheet).
 * ★ s99 (Dr. Rami 2026-10-10, "Orella Flow Edits"): View PDF and Share link are two pills side by
 *   side; the code line is Copy · Open QR (today's Case Report pass, SharePassModal, the claim URL
 *   as a QR drawn on the phone); "Send to your doctor's office" is a row with a chevron like
 *   "Need a Doctor?" — the navy "Find your doctor" pill is gone.
 */
export default function ShareHubScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const nextRef = useRef(location.state && location.state.next ? location.state.next : null)
  const { currentCase, updateAndSaveCase } = useCaseStore()
  const profile = useProfileStore((s) => s.profile)
  const { t, language } = useI18n()
  const [phase, setPhase] = useState('start') // start | before | building | error | hub
  const [step, setStep] = useState(null)
  const [overlay, setOverlay] = useState(null) // null | 'agreement' | 'pass' | 'withdraw' | 'needdoctor'
  const [pdfBusy] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [notice, setNotice] = useState('')
  const [sends, setSends] = useState([])
  const [failNotice, setFailNotice] = useState(null) // the failed send shown once
  const [withdrawing, setWithdrawing] = useState(false)
  // ★ s106 (TestFlight b72; mockup "Orella Doctors Tab Send", approved 2026-10-16): the office the
  //   patient chose on the Doctors tab (utils/preferredOffice) waits here, already the choice, until
  //   this case has an office send. The patient still reviews, consents and sends (SendReportScreen).
  const [chosenOffice, setChosenOffice] = useState(null)
  // s119 (canvas "Orella Share Code Approval"): P1 before the link / code leaves the app, and the opener requests.
  const [riskFor, setRiskFor] = useState(null) // null | 'link' | 'copy'
  const [openReq, setOpenReq] = useState(null) // { request, last } from GET /cases/<id>/open-request
  const [openedSheet, setOpenedSheet] = useState(false) // s126: the opener's directory record
  const [revokeAsk, setRevokeAsk] = useState(false)
  // s126: arriving from the "opened" notice (CaseRoute ?go=opened) opens the sheet once the opener has loaded.
  const wantOpenedSheet = useRef(!!(location.state && location.state.openedSheet))
  useEffect(() => {
    if (wantOpenedSheet.current && openedRecord(openReq)) { wantOpenedSheet.current = false; setOpenedSheet(true) }
  }, [openReq])
  const afterBuildRef = useRef(null) // where to go once a rebuilt code is ready
  const startedRef = useRef(false)

  const missing = missingBeforeShare(profile)
  const code = currentCase?.shareCode || null

  useEffect(() => { window.scrollTo(0, 0) }, [])

  // s119: is someone asking to open this case (P2), or who opened it (P3)? Asked again every 15 seconds.
  useEffect(() => {
    const id = currentCase?.id
    if (!id || !currentCase?.shareCode) { setOpenReq(null); return undefined }
    let alive = true
    const ask = () => casesAPI.getOpenRequest(id).then((d) => { if (alive) setOpenReq(d || null) }).catch(() => {})
    ask()
    const tm = setInterval(ask, 15000)
    return () => { alive = false; clearInterval(tm) }
  }, [currentCase?.id, currentCase?.shareCode])

  useEffect(() => {
    if (!currentCase || startedRef.current) return
    if (!code && !profile) return // the profile is still loading: decide once it is here
    if (code) { setPhase('hub'); return }
    if (missing.length) { setPhase('before'); return }
    startedRef.current = true
    runBuild()
  }, [currentCase?.id, code, missing.length, !!profile]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase !== 'hub' || !nextRef.current) return
    const n = nextRef.current
    nextRef.current = null
    // PROD-1362 follow-up: n comes from history state; a target safePath refuses keeps the hub open
    const to = safePath(n.to, null)
    if (to) navigate(to, { state: n.state, replace: true })
  }, [phase]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase !== 'hub' || !currentCase?.id) return
    let alive = true
    casesAPI.faxSends(currentCase.id)
      .then((r) => {
        if (!alive) return
        const list = Array.isArray(r?.sends) ? r.sends : []
        setSends(list)
        if (currentOfficeSend(list)) { clearPreferredOffice(); setChosenOffice(null) }
        else setChosenOffice(readPreferredOffice(currentCase.id))
        const failed = unseenFailedSend(list, readSeenFailures())
        if (failed) { setFailNotice(failed); markFailureSeen(failed.id) }
      })
      .catch(() => {})
    return () => { alive = false }
  }, [phase, currentCase?.id])

  // s99: `caseArg` is the case as it stands right after a withdrawal (the closure's currentCase
  //   still holds the dead code); a click event or nothing means the current case.
  async function runBuild(caseArg) {
    setPhase('building')
    setStep('summary')
    try {
      await buildCaseReport(caseArg && caseArg.id ? caseArg : currentCase, updateAndSaveCase, setStep)
      setStep('done')
      setPhase('hub')
      if (afterBuildRef.current) { const to = afterBuildRef.current; afterBuildRef.current = null; if (typeof to === 'string') navigate(to); else navigate(to.to, { state: to.state }) }
    } catch (e) {
      startedRef.current = false
      setPhase('error')
    }
  }

  function flash(msg) {
    setNotice(msg)
    setTimeout(() => setNotice(''), 2200)
  }

  // ★ s99 D477 (Build 61 TestFlight): View PDF opens the in-app viewer (CaseReportViewer). s102 D8:
  //   the viewer itself now has Download + Share (Dr. Rami list 3); openPdf only opens it. Before, this handed the file to the iPhone share sheet. The Document
  //   Sharing Agreement still comes first, once per case (viewPdf below, unchanged).
  function openPdf() {
    setViewerOpen(true)
  }

  function viewPdf() {
    const agreed = !!(currentCase?.visitPrepAgreed || currentCase?.visit_prep_agreed)
    if (agreed) openPdf()
    else setOverlay('agreement')
  }

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(code)
      flash(t('shareHub.codeCopied', 'Code copied'))
    } catch (e) {
      flash(code)
    }
  }

  async function shareLink() {
    const url = buildClaimUrl(code)
    const title = t('shareHub.reportTitle', 'Your Case Report')
    const text = t('shareHub.shareLinkText', { defaultValue: 'My Orella case for my visit (share code {{code}}):', code })
    try {
      if (Capacitor.isNativePlatform()) {
        const { Share } = await import('@capacitor/share')
        await Share.share({ title, text, url, dialogTitle: title })
        return
      }
      if (navigator.share) {
        await navigator.share({ title, text, url })
        return
      }
    } catch (e) {
      // A dismissed share sheet is not a failure; anything else falls back to copying.
      if (e && (e.name === 'AbortError' || /cancel/i.test(String(e.message || '')))) return
    }
    try {
      await navigator.clipboard.writeText(url)
      flash(t('shareHub.linkCopied', 'Link copied'))
    } catch (e) {
      flash(url)
    }
  }

  if (!currentCase) return <Navigate to="/home" replace />

  const passName = [profile?.identification?.firstName, profile?.identification?.lastName].filter(Boolean).join(' ')
  const passCaseLine = [currentCase.specialtyName || currentCase.specialty, currentCase.focusAreaName || currentCase.focus_area].filter(Boolean).join(' · ')

  // s99 D475: the office this report went to (null = not sent, or only failed sends).
  const office = currentOfficeSend(sends)
  const lines = officeLines(office)
  const doc = currentCase.doctor || null
  const acceptedBy = office && doc && ANSWERS_LOCKED_STATUSES.has(String(currentCase.status || ''))
    ? clinicianName(withCaseClinician(doc, currentCase), 'short') : ''
  const officeWho = lines ? (lines.name || lines.practice) : ''
  const bubble = phase === 'before'
    ? t('shareHub.beforeMsg', 'Almost there. Your doctor needs these before we send your case.')
    : phase === 'hub'
      ? (acceptedBy
          ? t('shareHub.acceptedMsg', { defaultValue: '{{name}} accepted your case. Their office will contact you to set up your visit.', name: acceptedBy })
          : office && office.status === 'delivered'
            ? t('shareHub.deliveredMsg', { defaultValue: "Your Case Report reached {{name}}'s office. They can open your full case with your share code.", name: officeWho })
            : office
              ? t('shareHub.sendingMsg', "Sending your Case Report now. I'll let you know when the office has it.")
              : t('shareHub.readyMsg', "Your Case Report is ready. Send it to your doctor's office, or view it and share it yourself."))
      : t('shareHub.buildingMsg', 'Building your Case Report before we share with your doctor.')

  // s99 D476: `toOrella` = "Need a Doctor?" after a send — withdraw, then the Orella list (its
  //   own send makes the next share; no code is built here). Otherwise a new code, then the finder.
  // s126 (Dr. Rami 2026-10-31, mockup "Orella Opened-By Card" APPROVED): revoke the clinician who opened the case by
  //   code — ONLY before they accept it (not claimed / scheduled / later: ANSWERS_LOCKED_STATUSES) and only where the
  //   one withdraw rule allows it (canWithdrawCase). The one withdraw (utils/withdrawCase), then a new code here.
  async function revokeOpener() {
    if (withdrawing) return
    setWithdrawing(true)
    try {
      await withdrawCaseFromDoctor(currentCase.id, updateAndSaveCase, currentCase)
      setRevokeAsk(false); setOpenedSheet(false); setOpenReq(null); setSends([])
      afterBuildRef.current = null
      startedRef.current = true
      runBuild({ ...currentCase, shareCode: null, shareExpiration: '', doctorSource: null, doctor_source: null })
    } catch (e) {
      flash(t('shareHub.withdrawFailed', "We couldn't end that office's access. Please try again."))
    } finally {
      setWithdrawing(false)
    }
  }

  async function withdrawAndChoose(toOrella) {
    if (withdrawing) return
    setWithdrawing(true)
    try {
      await withdrawCaseFromDoctor(currentCase.id, updateAndSaveCase, currentCase)
      setOverlay(null)
      setSends([])
      if (toOrella === true) { navigate('/case/share', { state: { findDoctor: true, afterWithdraw: true } }); return }
      // s101 Phase 4: the one search (the picker with its directory section), not the old finder.
      afterBuildRef.current = { to: '/case/share', state: { findDoctor: true } }
      startedRef.current = true
      runBuild({ ...currentCase, shareCode: null, shareExpiration: '', doctorSource: null, doctor_source: null })
    } catch (e) {
      flash(t('shareHub.withdrawFailed', "We couldn't end that office's access. Please try again."))
    } finally {
      setWithdrawing(false)
    }
  }

  // s126: the latest opener of the current code (allowed, or its 15 minutes over), and whether Revoke is offered.
  // PROD-1418: the sheet renders these fields as text; a field the API sends in another shape reads as absent, never
  //   as a React child that throws (openedRecord), and a date that does not parse shows no time.
  const openedLast = openedRecord(openReq)
  const openedWho = openedLast ? clinicianForms(clinicianFromText(openedLast.clinicianTitle)) : { clinician: '', clinicianOf: '' }
  const openedAt = openedLast?.requestedAt ? new Date(openedLast.requestedAt) : null
  const openedWhen = openedAt && !Number.isNaN(openedAt.getTime()) ? openedAt.toLocaleString(appLocale(language), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''
  const openedPlace = openedLast ? [openedLast.practice, [openedLast.city, openedLast.stateCode].filter(Boolean).join(', ')].filter(Boolean).join(', ') : ''
  // s126b (TestFlight build 97, Dr. Rami: "Doctor read the case but took no action … I'd like for the patient to be able to revoke
  //   access … cancel and share with another clinician"): a case shared BY CODE keeps the status 'ready_to_share' until someone
  //   claims it, and canWithdrawCase refuses 'ready_to_share' — so Revoke hid exactly when nobody had accepted. The status is
  //   resolved the way the header pill resolves it (resolveShareStatus: a live code = 'shared') before the one withdraw rule asks.
  const openedEnded = !!openedLast && openedLast.state === 'locked' // the 15 minutes are over and nobody accepted or declined
  const canRevokeOpener = !!openedLast
    && canWithdrawCase({ ...currentCase, status: resolveShareStatus(String(currentCase.status || ''), currentCase) })
    && !ANSWERS_LOCKED_STATUSES.has(String(currentCase.status || ''))

  const chips = [
    ['profile', t('diagnose.chipProfile', 'Profile')],
    ['symptoms', t('diagnose.chipSymptoms', 'Symptoms')],
    ['photos', t('diagnose.chipPhotos', 'Photos')],
    ['summary', t('diagnose.chipAiSummary', 'AI Summary')],
    ['code', t('diagnose.chipShareCode', 'Share Code')],
  ]
  const at = step === 'done' ? BUILD_STEPS.length : Math.max(0, BUILD_STEPS.indexOf(step))

  return (
    <div className="min-h-full bg-gray-50 flex flex-col">
      <ChatKeyframes />
      <CompactHeader
        stepLabel={t('shareHub.stepLabel', 'Share')}
        onBack={() => caseBack(navigate)}
        onNotification={() => navigate('/notifications')}
      />
      <OrellaChatBubble message={bubble} animationKey={`share-hub-${phase}-${currentCase.id}`} instant />

      <div className="flex-1 px-4 pt-1 pb-6 flex flex-col gap-3">
        {phase === 'before' && (
          <div className="bg-white rounded-2xl border-[1.5px] border-amber-200 p-5">
            <h3 className="text-[15px] font-bold mb-1" style={{ color: NAVY }}>{t('diagnose.beforeShareTitle', 'Before we share your case')}</h3>
            <p className="text-[13px] text-gray-500 mb-3">{t('diagnose.beforeShareBody', 'Your doctor needs these on file first:')}</p>
            <ul className="text-[13.5px] mb-4 space-y-1" style={{ color: NAVY }}>
              {missing.map((id) => (
                <li key={id}>{{
                  identification: t('profile.identification'),
                  insurance: t('profile.insurance'),
                  allergies: t('profile.allergies'),
                  medications: t('profile.medications'),
                }[id]}</li>
              ))}
            </ul>
            <button
              onClick={() => navigate(missing.includes('identification') ? '/profile?guided=true' : '/onboarding?run=profile')}
              className="w-full py-3 rounded-full font-bold text-white text-[14px] active:scale-[0.98]"
              style={{ background: NAVY }}
            >
              {t('diagnose.beforeShareCta', 'Complete my profile')}
            </button>
            {/* PROD-1132 Q1 (Alex 2026-10-18): self-pay right here. A saved answer drops insurance from
                `missing`; the start effect above re-runs on missing.length and builds when nothing is left. */}
            <ContinueWithoutInsurance missing={missing} className="mt-2" />
          </div>
        )}

        {(phase === 'building' || phase === 'start') && (
          <div className="bg-white rounded-[20px] border border-[#E5EDF3] px-4 py-6 flex flex-col items-center gap-2 text-center" data-testid="share-hub-building">
            <OrellaSpinner size={56} label={t('shareHub.buildingTitle', 'Building your Case Report')} />
            <h3 className="text-[16px] font-bold mt-1" style={{ color: NAVY }}>{t('shareHub.buildingTitle', 'Building your Case Report')}</h3>
            <p className="text-[12px]" style={{ color: MUTE }}>{t('shareHub.buildingSub', 'Compiling your history, photos and summary, then your share code')}</p>
            <div className="flex flex-wrap justify-center gap-1.5 mt-1">
              {chips.map(([id, label], i) => {
                const done = id === 'profile' || id === 'symptoms' || id === 'photos' || i < at
                const run = !done && BUILD_STEPS[at] === id
                return (
                  <span key={id} className="text-[12px] font-bold rounded-full px-3 py-1"
                        style={{ background: done ? '#E5F7EA' : run ? ICE : '#F1F5F8', color: done ? '#2A9B46' : run ? BLUE : '#9AABBA' }}>
                    {done ? '✓ ' : ''}{label}
                  </span>
                )
              })}
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div className="bg-white rounded-2xl border-[1.5px] border-amber-200 p-5 text-center">
            <AlertTriangle size={26} className="mx-auto mb-2" style={{ color: '#B45309' }} />
            <h3 className="text-[15px] font-bold mb-1" style={{ color: NAVY }}>{t('diagnose.generateFailedTitle', "We couldn't build your Case Report")}</h3>
            <button onClick={runBuild} className="mt-3 w-full py-3 rounded-full font-bold text-white text-[14px]" style={{ background: NAVY }}>
              {t('diagnose.generateRetry', 'Try again')}
            </button>
          </div>
        )}

        {phase === 'hub' && (
          <>
            <div className="bg-white rounded-[20px] border border-[#E5EDF3] p-4 flex flex-col gap-3.5" data-testid="share-hub-report">
              <div className="flex items-center gap-3">
                <span className="relative flex-none rounded-[7px] border border-[#D8E4EE] bg-white flex flex-col gap-1 px-1.5 py-2"
                      style={{ width: 46, height: 58, boxShadow: '0 3px 8px -3px rgba(21,53,79,.25)' }} aria-hidden="true">
                  <i className="block h-[3px] rounded" style={{ background: NAVY, width: '70%' }} />
                  <i className="block h-[3px] rounded bg-[#DCE6EF]" /><i className="block h-[3px] rounded bg-[#DCE6EF]" />
                  <i className="block h-[3px] rounded bg-[#DCE6EF]" style={{ width: '60%' }} />
                  <em className="absolute not-italic text-[8px] font-extrabold text-white rounded px-1" style={{ bottom: -5, right: -6, background: '#E5484D' }}>PDF</em>
                </span>
                <span className="flex-1 min-w-0">
                  <b className="block text-[16px] font-bold" style={{ color: NAVY }}>{t('shareHub.reportTitle', 'Your Case Report')}</b>
                  {currentCase.shareExpiration ? (
                    <small className="block text-[13px] mt-0.5" style={{ color: BODY }}>
                      {t('shareHub.codeExpires', { defaultValue: 'Code expires {{date}}', date: currentCase.shareExpiration })}
                    </small>
                  ) : null}
                </span>
              </div>
              {/* s126: who opened the case — one tappable row (the old one-line "Opened by"), details in a sheet. */}
              {openedLast ? (
                <button type="button" data-opened-by="" onClick={() => setOpenedSheet(true)}
                        className="w-full flex items-center gap-2.5 text-left rounded-xl border border-[#C7E5FD] bg-[#E9F8FF] px-3 py-2">
                  <span className="w-6 h-6 rounded-full grid place-items-center flex-none text-[12px] font-extrabold" style={{ background: '#D5EFD3', color: '#16A34A' }}>✓</span>
                  <span className="flex-1 min-w-0">
                    <b className="block text-[13.5px]" style={{ color: NAVY }}>{t('shareApproval.openedBy', { ...openedWho, time: openedWhen })}</b>
                    <small className="block text-[12px]" style={{ color: BODY }}>{openedEnded ? t('shareApproval.accessEnded', 'Access ended') : t('shareApproval.noticeReadOnly')}</small>
                  </span>
                  <ChevronRight size={18} style={{ color: '#46A8F9' }} />
                </button>
              ) : null}
              {openReq?.request ? (
                <button type="button" onClick={() => navigate(`/case/${currentCase.id}/allow-access`)}
                        className="w-full text-left rounded-2xl border-[1.5px] border-[#D6ECFB] bg-[#EFF8FF] px-4 py-3" data-open-request="">
                  <b className="block text-[15px]" style={{ color: NAVY }}>{t('shareApproval.allowTitle')}</b>
                  <span className="block text-[13px] mt-0.5" style={{ color: BODY }}>{t('shareApproval.wantsToOpen', clinicianForms(clinicianFromText(openReq.request.clinicianTitle || '')))}</span>
                </button>
              ) : null}
              <ShareRiskSheet open={!!riskFor} t={t}
                confirmLabel={riskFor === 'copy' ? t('shareHub.copy', 'Copy') : t('shareApproval.shareLink')}
                onCancel={() => setRiskFor(null)}
                onConfirm={() => { const what = riskFor; setRiskFor(null); if (what === 'copy') copyCode(); else shareLink() }} />
              {/* ★ PROD-1424 (Alex 2026-11-03): "Find a doctor" is what the patient is here to tap, so it is the main
                  button: a full-width navy pill like View PDF / Share link, ABOVE them, inside the report card. Same
                  tap as before (an office send -> the 'needdoctor' sheet, D476; else the Orella search). This reverses
                  Dr. Rami's s99 "Orella Flow Edits" row-with-chevron (FYI on list 7, F1424). s101 Phase 4 still holds:
                  ONE doctor search, one button. */}
              <div className="flex flex-col gap-1.5">
                <button onClick={() => (office ? setOverlay('needdoctor') : navigate('/case/share', { state: { findDoctor: true } }))} data-cta="share-hub-need-doctor"
                        className="w-full h-[50px] rounded-full font-bold text-[15px] text-white flex items-center justify-center gap-2 active:scale-[0.98]"
                        style={{ background: NAVY }}>
                  <Stethoscope size={18} />
                  {t('shareHub.findDoctor', 'Find a doctor')}
                </button>
                {/* R1422 L1: BODY (#5A7185, ~5.1:1 on white) - MUTE (~2.5:1) is under WCAG AA 4.5:1 for this load-bearing line. */}
                <small data-testid="share-hub-find-doctor-sub" className="block text-center text-[12px] leading-snug px-2" style={{ color: BODY }}>
                  {t('shareHub.findDoctorSub', 'Search any doctor in the US. Orella doctors get your case in the app; anyone else gets it by fax.')}
                </small>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <button onClick={viewPdf} disabled={pdfBusy}
                        className="h-[50px] rounded-full font-bold text-[15px] text-white flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-70"
                        style={{ background: NAVY }} data-cta="share-hub-view-pdf">
                  {pdfBusy ? <OrellaSpinner size={18} tone="inherit" /> : <Eye size={18} />}
                  {t('shareHub.viewPdf', 'View PDF')}
                </button>
                <button onClick={() => setRiskFor('link')}
                        className="h-[50px] rounded-full font-bold text-[15px] text-white flex items-center justify-center gap-2 active:scale-[0.98]"
                        style={{ background: NAVY }} data-cta="share-hub-share-link">
                  <Share2 size={18} />
                  {t('shareHub.shareLink', 'Share link')}
                </button>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap border-t border-[#EEF3F7] pt-2.5 text-[12px]" style={{ color: MUTE }}>
                <span>{t('shareHub.shareCode', 'Share code')}</span>
                <b className="text-[13px] font-bold tracking-wide tabular-nums" style={{ color: BODY }}>{code}</b>
                <span className="flex-1" />
                <button onClick={() => setRiskFor('copy')} className="font-bold" style={{ color: BLUE }}>{t('shareHub.copy', 'Copy')}</button>
                <button onClick={() => setOverlay('pass')} className="font-bold ml-3" style={{ color: BLUE }} data-cta="share-hub-open-qr">{t('shareHub.openQr', 'Open QR')}</button>
              </div>
            </div>

            {office ? (
              <>
                <div className="bg-white rounded-[20px] p-4 flex flex-col gap-3" data-testid="share-hub-office"
                     style={{ border: `1.5px solid ${LBLUE}` }}>
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-full grid place-items-center flex-none text-[14px] font-extrabold" style={{ background: '#DCEBF7', color: NAVY }}>
                      {initialsOf(lines.name || lines.practice)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[10.5px] font-extrabold uppercase tracking-[.08em]" style={{ color: MUTE }}>{t('shareHub.yourOffice', "Your doctor's office")}</span>
                      <b className="block text-[15.5px] font-bold leading-tight" style={{ color: NAVY }}>{lines.name || lines.practice}</b>
                      {lines.specialty ? <small className="block text-[12.5px]" style={{ color: BODY }}>{lines.specialty}</small> : null}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5 text-[13px] border-t border-[#EEF3F7] pt-2.5" style={{ color: NAVY }}>
                    {lines.practice && lines.practice !== lines.name ? (
                      <span className="flex gap-2 items-start"><Building2 size={15} className="flex-none mt-0.5" style={{ color: MUTE }} />{lines.practice}</span>
                    ) : null}
                    {lines.street || lines.cityLine ? (
                      <span className="flex gap-2 items-start"><MapPin size={15} className="flex-none mt-0.5" style={{ color: MUTE }} />
                        <span>{lines.street}{lines.street && lines.cityLine ? <br /> : null}{lines.cityLine}</span></span>
                    ) : null}
                    {lines.phone ? (
                      <a href={`tel:${String(lines.phone).replace(/[^0-9+]/g, '')}`} className="flex gap-2 items-center font-bold" style={{ color: BLUE }}>
                        <Phone size={15} className="flex-none" style={{ color: MUTE }} />{formatUsPhone(lines.phone)}
                      </a>
                    ) : null}
                    {lines.fax ? (
                      <span className="flex gap-2 items-center" style={{ color: BODY }}><Printer size={15} className="flex-none" style={{ color: MUTE }} />
                        {t('shareHub.faxLine', { defaultValue: 'Fax {{number}}', number: formatUsPhone(lines.fax) })}</span>
                    ) : null}
                  </div>
                  {acceptedBy ? (
                    <span className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-[12.5px] font-bold" style={{ background: '#EAF7EE', color: '#2A9B46' }}>
                      <Check size={14} />{t('shareHub.acceptedBy', { defaultValue: 'Accepted by {{name}}', name: acceptedBy })}
                    </span>
                  ) : office.status === 'delivered' ? (
                    <span className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-[12.5px] font-bold" style={{ background: '#EAF7EE', color: '#2A9B46' }}>
                      <Check size={14} />{t('shareHub.deliveredOn', { defaultValue: 'Delivered · {{when}}', when: whenOf(office.deliveredAt || office.updatedAt) })}
                    </span>
                  ) : (
                    <button type="button" onClick={() => navigate('/case/send-report', { state: { sent: office } })}
                            className="flex items-center gap-2 rounded-xl px-3 py-2 text-[12.5px] font-bold text-left" style={{ background: ICE, color: '#1F5F99' }}>
                      <OrellaSpinner size={16} tone="inherit" />{t('shareHub.sendingNow', 'Sending your Case Report…')}
                    </button>
                  )}
                </div>
                <button type="button" onClick={() => setOverlay('withdraw')} data-cta="share-hub-different-office"
                        className="self-start text-[12.5px] font-bold -mt-1" style={{ color: BLUE }}>
                  {t('shareHub.differentOffice', 'Send to a different office')}
                </button>
              </>
            ) : chosenOffice ? (
              <>
                <div className="bg-white rounded-[20px] p-4 flex flex-col gap-3" data-testid="share-hub-chosen-office"
                     style={{ border: `1.5px solid ${LBLUE}` }}>
                  <span className="block text-[10.5px] font-extrabold uppercase tracking-[.08em]" style={{ color: MUTE }}>{t('shareHub.chosenOffice', 'Send to the office you chose')}</span>
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-full grid place-items-center flex-none text-[14px] font-extrabold" style={{ background: '#DCEBF7', color: NAVY }}>
                      {initialsOf(chosenOffice.name)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <b className="block text-[15.5px] font-bold leading-tight" style={{ color: NAVY }}>{chosenOffice.name}{chosenOffice.credential ? `, ${chosenOffice.credential}` : ''}</b>
                      {chosenOffice.specialty ? <small className="block text-[12.5px]" style={{ color: BODY }}>{chosenOffice.specialty}</small> : null}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5 text-[13px] border-t border-[#EEF3F7] pt-2.5" style={{ color: NAVY }}>
                    {chosenOffice.address && (chosenOffice.address.line1 || chosenOffice.address.city) ? (
                      <span className="flex gap-2 items-start"><MapPin size={15} className="flex-none mt-0.5" style={{ color: MUTE }} />
                        <span>{[chosenOffice.address.line1, chosenOffice.address.line2].filter(Boolean).join(', ')}{chosenOffice.address.line1 ? <br /> : null}{[chosenOffice.address.city, [chosenOffice.address.state, chosenOffice.address.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</span></span>
                    ) : null}
                    {chosenOffice.phone ? (
                      <a href={`tel:${String(chosenOffice.phone).replace(/[^0-9+]/g, '')}`} className="flex gap-2 items-center font-bold" style={{ color: BLUE }}>
                        <Phone size={15} className="flex-none" style={{ color: MUTE }} />{formatUsPhone(chosenOffice.phone)}
                      </a>
                    ) : null}
                    <span className="flex gap-2 items-center" style={{ color: chosenOffice.faxOnFile ? BODY : '#B45309' }}><Printer size={15} className="flex-none" style={{ color: MUTE }} />
                      {chosenOffice.faxOnFile ? t('findDoctor.faxOnFile', 'Fax on file') : t('findDoctor.noFax', 'No fax listed')}</span>
                  </div>
                  <button type="button" onClick={() => navigate('/case/send-report', { state: { provider: chosenOffice } })} data-cta="share-hub-chosen-office-send"
                          className="h-[46px] rounded-full font-bold text-[14.5px] text-white flex items-center justify-center gap-2 active:scale-[0.98]"
                          style={{ background: NAVY }}>
                    <Printer size={17} /> {t('shareHub.reviewAndSendFax', 'Review and send by fax')}
                  </button>
                </div>
                <button type="button" onClick={() => { clearPreferredOffice(); setChosenOffice(null); navigate('/case/share', { state: { findDoctor: true } }) }}
                        data-cta="share-hub-chosen-office-change" className="self-start text-[12.5px] font-bold -mt-1" style={{ color: BLUE }}>
                  {t('shareHub.chooseDifferentOffice', 'Choose a different office')}
                </button>
              </>
            ) : null}
          </>
        )}
      </div>

      <div className="flex-none px-4 pt-2.5 pb-5 bg-white border-t border-[#EEF3F7]">
        <button onClick={() => navigate('/case/home')}
                className="w-full h-[50px] rounded-full font-bold text-[15px] text-white flex items-center justify-center gap-2"
                style={{ background: BLUE }}>
          <Home size={18} /> {t('visitPrep.caseHome', 'Case Home')}
        </button>
      </div>

      {notice && (
        <OverlayPortal><div role="status" className="fixed left-4 right-4 bottom-24 rounded-xl px-3.5 py-2.5 text-[13px] text-white z-50" style={{ background: NAVY }}>{notice}</div></OverlayPortal>
      )}

      <SharePassModal
        isOpen={overlay === 'pass' && !!code}
        onClose={() => setOverlay(null)}
        patientName={passName}
        caseLine={passCaseLine}
        shareCode={code}
        expiresLabel={currentCase.shareExpiration || ''}
        qrValue={code ? buildClaimUrl(code) : ''}
        instruction={t('visitPrep.scanToOpenYourCase')}
      />

      {failNotice && phase === 'hub' && (
        <OverlayPortal>
          <div role="status" className="fixed left-4 right-4 top-16 z-50 rounded-xl px-3.5 py-3 flex gap-2.5 items-start"
               style={{ background: '#FFF7E8', border: '1px solid #F5D9A8', color: '#8A4B0A' }}>
            <AlertTriangle size={16} className="flex-none mt-0.5" />
            <span className="flex-1 text-[13px] leading-snug">
              {(officeLines(failNotice) || {}).name
                ? t('shareHub.failedOnce', { defaultValue: "Your Case Report didn't reach {{name}}'s office.", name: officeLines(failNotice).name })
                : t('shareHub.failedOnceGeneric', "Your Case Report didn't reach the office.")}{' '}
              <button type="button" className="font-bold underline" onClick={() => { const f = failNotice; setFailNotice(null); navigate('/case/send-report', { state: { retry: f } }) }}>
                {t('shareHub.tryAgain', 'Try again')}
              </button>
            </span>
            <button type="button" aria-label={t('common.close', 'Close')} onClick={() => setFailNotice(null)} className="p-0.5"><X size={16} /></button>
          </div>
        </OverlayPortal>
      )}

      <SwitchDoctorSheet
        open={overlay === 'withdraw' && !!office}
        testId="share-hub-withdraw"
        title={t('shareHub.differentOfficeTitle', 'Send to a different office?')}
        body1={t('shareHub.differentOfficeBody1', { defaultValue: "First we'll end {{name}}'s access to your case. Your share code stops working, and they keep the faxed report they already received.", name: officeWho })}
        body2={t('shareHub.differentOfficeBody2', "Then you'll choose your new doctor, and we'll make a new share code for them.")}
        confirmLabel={t('shareHub.differentOfficeConfirm', 'End access and choose a new doctor')}
        keepLabel={t('shareHub.keepOffice', { defaultValue: 'Keep {{name}}', name: officeWho })}
        busy={withdrawing}
        onConfirm={() => withdrawAndChoose(false)}
        onClose={() => setOverlay(null)}
      />
      <SwitchDoctorSheet
        open={overlay === 'needdoctor' && !!office}
        testId="share-hub-need-doctor-sheet"
        title={t('shareHub.orellaInsteadTitle', 'Choose an Orella doctor instead?')}
        body1={t('shareHub.orellaInsteadBody1', { defaultValue: "{{name}}'s office already has your Case Report. To book with an Orella doctor, we'll first end that office's access. Your share code stops working; they keep the fax they received.", name: officeWho })}
        body2={t('shareHub.orellaInsteadBody2', 'Then you can pick an Orella doctor and book with them directly in the app.')}
        confirmLabel={t('shareHub.orellaInsteadConfirm', 'End access and see Orella doctors')}
        keepLabel={t('shareHub.keepOffice', { defaultValue: 'Keep {{name}}', name: officeWho })}
        busy={withdrawing}
        onConfirm={() => withdrawAndChoose(true)}
        onClose={() => setOverlay(null)}
      />

      <CaseReportViewer open={viewerOpen} caseObj={currentCase} onClose={() => setViewerOpen(false)} t={t}
        footNote={t('reportViewer.shareHint', 'To send it to someone, use Share link.')} />

      {overlay === 'agreement' && (
        <OverlayPortal>
        <div className="fixed inset-0 z-50 flex items-end" style={{ background: 'rgba(11,22,32,.45)' }}>
          <div className="w-full max-h-[88%] overflow-y-auto bg-gray-50 rounded-t-3xl p-4 bottom-actions">
            <div className="flex justify-end mb-1">
              <button aria-label={t('common.close', 'Close')} onClick={() => setOverlay(null)} className="p-1"><X size={20} style={{ color: NAVY }} /></button>
            </div>
            <VisitPrepDisclaimer
              onAgree={async () => {
                const at2 = new Date().toISOString()
                await updateAndSaveCase({ visitPrepAgreed: true, visitPrepAgreedAt: at2 })
                await casesAPI.update(currentCase.id, { visit_prep_agreed: true, visit_prep_agreed_at: at2 })
                setOverlay(null)
                openPdf()
              }}
              onCancel={() => setOverlay(null)}
            />
          </div>
        </div>
        </OverlayPortal>
      )}

      {/* s126: who opened the case (mockup "Orella Opened-By Card" rev 2, APPROVED 2026-10-31). */}
      {openedSheet && openedLast && (
        <BottomSheet open onClose={() => setOpenedSheet(false)} maxHeight="88dvh">
          <div className="px-4 pb-6 flex flex-col gap-3.5" data-testid="opened-by-sheet">
            <div className="flex gap-2.5 items-start">
              <img src={orellaOIcon} alt="" className="w-9 h-9 rounded-[10px] flex-none" />
              <div className="flex-1 rounded-[2px_16px_16px_16px] border border-[#C7E5FD] bg-[#E9F8FF] px-3.5 py-2.5">
                <b className="block text-[15px]" style={{ color: NAVY }}>{t('shareApproval.noticeOpened', openedWho)}</b>
                <span className="block text-[13px] mt-0.5" style={{ color: BODY }}>{openedEnded
                  ? t('shareApproval.accessEndedBody', 'Their read-only access has ended. To send your case to another clinician, make a new code.')
                  : t('shareApproval.noticeReadOnly')}</span>
              </div>
            </div>
            <dl className="grid gap-x-4 gap-y-1.5 text-[14px] ml-[46px]" style={{ gridTemplateColumns: 'auto 1fr' }}>
              {openedLast.specialty ? <><dt className="text-[#456178]">{t('shareApproval.labelSpecialty')}</dt><dd className="m-0 font-semibold" style={{ color: NAVY }}>{openedLast.specialty}</dd></> : null}
              {openedPlace ? <><dt className="text-[#456178]">{t('shareApproval.labelOffice')}</dt><dd className="m-0 font-semibold" style={{ color: NAVY }}>{openedPlace}</dd></> : null}
              {openedLast.npi ? <><dt className="text-[#456178]">{t('shareApproval.labelNpi')}</dt><dd className="m-0 font-semibold" style={{ color: NAVY }}>{openedLast.npi}</dd></> : null}
              {openedWhen ? <><dt className="text-[#456178]">{t('shareApproval.labelOpened', 'Opened')}</dt><dd className="m-0 font-semibold" style={{ color: NAVY }}>{openedWhen}</dd></> : null}
            </dl>
            {openedLast.role === 'staff' && openedLast.staffName ? (
              <p className="m-0 ml-[46px] text-[13px]" style={{ color: BODY }}>
                {t('shareApproval.staffLine', { ...openedWho, name: openedLast.staffName, role: openedLast.staffTitle || '' })}
              </p>
            ) : null}
            {openedEnded && canRevokeOpener ? (
              <button type="button" data-opened-reshare="" disabled={withdrawing} onClick={() => { setOpenedSheet(false); withdrawAndChoose(false) }}
                      className="w-full h-12 rounded-full text-white text-[15px] font-bold disabled:opacity-60" style={{ background: NAVY }}>
                {t('shareApproval.shareAnother', 'Share with another clinician')}
              </button>
            ) : null}
            <button type="button" onClick={() => setOpenedSheet(false)}
                    className={openedEnded && canRevokeOpener ? 'w-full py-1.5 text-[14px] font-semibold' : 'w-full h-12 rounded-full text-white text-[15px] font-bold'}
                    style={openedEnded && canRevokeOpener ? { color: NAVY } : { background: NAVY }}>
              {t('common.done', 'Done')}
            </button>
            {canRevokeOpener && !openedEnded ? (
              <button type="button" data-opened-revoke="" onClick={() => setRevokeAsk(true)} className="w-full py-1.5 text-[13.5px] font-semibold" style={{ color: '#C0262D' }}>
                {t('share.revokeAccess')}
              </button>
            ) : null}
          </div>
        </BottomSheet>
      )}
      <ConfirmModal
        open={revokeAsk}
        title={t('shareApproval.revokeTitle', { ...openedWho, defaultValue: "Revoke {{clinician}}'s access?" })}
        message={t('shareApproval.revokeBody', "They can no longer open your case, and your old code stops working. We'll make you a new code to send to your doctor.")}
        confirmLabel={t('share.revokeAccess')}
        cancelLabel={t('common.cancel')}
        confirmColor="#EF4444"
        loading={withdrawing}
        icon="warning"
        onConfirm={revokeOpener}
        onCancel={() => { if (!withdrawing) setRevokeAsk(false) }}
      />
    </div>
  )
}

// PROD-1418: the latest opener of the current code (GET /cases/<id>/open-request `last`: allowed, or its 15 minutes
//   over) with every field the sheet draws as a string ('' when missing or not text); null when there is none.
const OPENED_TEXT_FIELDS = ['clinicianTitle', 'specialty', 'npi', 'practice', 'city', 'stateCode', 'staffName', 'staffTitle']
function openedRecord(openReq) {
  const l = openReq && typeof openReq === 'object' && openReq.last && typeof openReq.last === 'object' ? openReq.last : null
  if (!l || (l.state !== 'allowed' && l.state !== 'locked')) return null
  const out = { ...l }
  for (const k of OPENED_TEXT_FIELDS) out[k] = typeof l[k] === 'string' || typeof l[k] === 'number' ? String(l[k]) : ''
  return out.clinicianTitle ? out : null
}

function initialsOf(name) {
  const parts = String(name || '').replace(/^dr\.?\s+/i, '').split(/[\s,]+/).filter((w) => w && !/^(md|do|np|pa|dds|dmd|dpm)$/i.test(w))
  return (((parts[0] || '')[0] || '') + ((parts[parts.length - 1] || '')[0] || '')).toUpperCase()
}

function whenOf(iso) {
  const d = iso ? new Date(iso) : null
  if (!d || Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
